import {
    type ComponentType,
    createContext,
    useContext
} from "react";
import type {
    ImageProps
} from "react-native";
import {
    type NCoreUIKitIcon,
    NCoreUIKitTheme,
    AnimatedIcon,
    Icon
} from "ncore-ui-kit";
import {
    UnfoldHorizontal,
    type LucideIcon,
    FoldHorizontal,
    ExternalLink,
    Smartphone,
    RotateCw,
    Monitor,
    Tablet,
    Search,
    Menu,
    Moon,
    Sun,
    X
} from "lucide-react-native";
import logoUrl from "../assets/ncore-logo.svg";

export const createKitIcon = (icon: LucideIcon): NCoreUIKitIcon => {
    const KitIcon: NCoreUIKitIcon = ({
        customColor,
        size
    }) => <Icon
        customColor={customColor}
        size={size}
        icon={icon}
    />;

    return KitIcon;
};

export const createActionIcon = (icon: LucideIcon): NCoreUIKitIcon => {
    const ActionIcon: NCoreUIKitIcon = ({
        customColor,
        size
    }) => <AnimatedIcon
        customColor={customColor}
        animationTrigger="hover"
        animation="signature"
        icon={icon}
        size={size}
    />;

    return ActionIcon;
};

export const createTransitionIcon = (fromIcon: LucideIcon, toIcon: LucideIcon) => {
    const TransitionContext = createContext(false);

    const TransitionIcon: NCoreUIKitIcon = ({
        customColor,
        size
    }) => {
        const isTransitioned = useContext(TransitionContext);

        return <AnimatedIcon
            isTransitioned={isTransitioned}
            customColor={customColor}
            iconTransition="morph"
            fromIcon={fromIcon}
            toIcon={toIcon}
            size={size}
        />;
    };

    return {
        Provider: TransitionContext.Provider,
        Icon: TransitionIcon
    };
};

export const createSwapIcon = (icons: Record<string, LucideIcon>) => {
    const SwapContext = createContext(Object.keys(icons)[0] ?? "");

    const SwapIcon: NCoreUIKitIcon = ({
        customColor,
        size
    }) => {
        const key = useContext(SwapContext);

        const icon = icons[key] ?? Object.values(icons)[0] ?? Sun;

        return <AnimatedIcon
            customColor={customColor}
            animationTrigger="mount"
            animation="appear"
            icon={icon}
            size={size}
            key={key}
        />;
    };

    return {
        Provider: SwapContext.Provider,
        Icon: SwapIcon
    };
};

export const LogoImage: ComponentType<ImageProps> = ({
    height,
    width
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <span
        style={{
            WebkitMask: `url("${logoUrl}") center / contain no-repeat`,
            mask: `url("${logoUrl}") center / contain no-repeat`,
            backgroundColor: colors.content.text.high,
            display: "inline-block",
            flexShrink: 0,
            height,
            width
        }}
        aria-hidden={true}
    />;
};

export const SearchKitIcon = createKitIcon(Search);

export const ExternalKitIcon = createKitIcon(ExternalLink);

export const SmartphoneKitIcon = createKitIcon(Smartphone);

export const MonitorKitIcon = createKitIcon(Monitor);

export const TabletKitIcon = createKitIcon(Tablet);

export const ReloadKitIcon = createKitIcon(RotateCw);

export const UnfoldKitIcon = createKitIcon(UnfoldHorizontal);

export const FoldKitIcon = createKitIcon(FoldHorizontal);

export const MenuTransition = createTransitionIcon(Menu, X);

export const ThemeSwap = createSwapIcon({
    system: Monitor,
    light: Sun,
    dark: Moon
});
